import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  X,
  Trophy,
  Target,
  Crown,
  Medal,
  Award,
  Sparkles,
  Calendar,
  Layers,
  Info,
} from 'lucide-react';
import { getLeaderboardRecords, LEADERBOARD_TITLES, PUZZLE_LEVELS } from '../data/leaderboards';

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialGame?: 'tower' | 'competition' | 'draw' | 'puzzle';
}

/**
 * Traditional Vietnamese Cloud Ribbon & Stylized Lotus Divider (Mây lượn Đại Việt & Hoa sen)
 * Inspired by decorative carvings from the Lý - Trần - Lê dynasties.
 */
const DaiVietLotusCloudDivider: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    viewBox="0 0 320 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`w-full max-w-[280px] sm:max-w-[340px] h-4 mx-auto text-[#e6ba55]/70 ${className}`}
    aria-hidden="true"
  >
    {/* Left cloud ribbon */}
    <path
      d="M10 10C25 10 35 4 50 4C65 4 70 15 85 15C100 15 110 8 125 8C135 8 140 10 148 10"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M25 10C35 10 40 14 50 14C60 14 65 7 75 7"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      opacity="0.5"
    />
    {/* Center stylized lotus */}
    <circle cx="160" cy="10" r="2.5" fill="currentColor" />
    <path
      d="M160 2C156 6 154 9 154 12C157 12 160 11 160 11C160 11 163 12 166 12C166 9 164 6 160 2Z"
      fill="currentColor"
    />
    <path
      d="M151 8C149 10 148 13 151 16C153 15 155 13 155 12C153 11 152 9 151 8Z"
      fill="currentColor"
      opacity="0.8"
    />
    <path
      d="M169 8C171 10 172 13 169 16C167 15 165 13 165 12C167 11 168 9 169 8Z"
      fill="currentColor"
      opacity="0.8"
    />
    {/* Right cloud ribbon */}
    <path
      d="M310 10C295 10 285 4 270 4C255 4 250 15 235 15C220 15 210 8 195 8C185 8 180 10 172 10"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M295 10C285 10 280 14 270 14C260 14 255 7 245 7"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      opacity="0.5"
    />
  </svg>
);

/**
 * Traditional Lotus Corner Flourish for Lacquer Frames
 */
const CornerLotusOrnament: React.FC<{ position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }> = ({
  position,
}) => {
  const positionClass = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2 rotate-90',
    'bottom-right': 'bottom-2 right-2 rotate-180',
    'bottom-left': 'bottom-2 left-2 -rotate-90',
  }[position];

  return (
    <div className={`absolute ${positionClass} pointer-events-none w-5 h-5 text-[#e6ba55]/30 select-none`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M2 22V6C2 3.79 3.79 2 6 2H22" strokeLinecap="round" />
        <circle cx="8" cy="8" r="2" fill="currentColor" opacity="0.6" />
        <path d="M5 14C8 12 12 8 14 5" opacity="0.4" strokeLinecap="round" />
      </svg>
    </div>
  );
};

export const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  isOpen,
  onClose,
  initialGame = 'tower',
}) => {
  const [activeGame, setActiveGame] = useState<'tower' | 'competition' | 'draw' | 'puzzle'>(initialGame);
  const [season, setSeason] = useState<'all' | 'season'>('all');
  const [puzzleIdx, setPuzzleIdx] = useState<number>(0);
  const [highlightUserRow, setHighlightUserRow] = useState<boolean>(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const userRowRef = useRef<HTMLTableRowElement | null>(null);

  // Sync initial game if modal is reopened
  useEffect(() => {
    if (isOpen) {
      setActiveGame(initialGame);
      setHighlightUserRow(false);
    }
  }, [isOpen, initialGame]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const records = useMemo(() => {
    return getLeaderboardRecords(activeGame, season, puzzleIdx);
  }, [activeGame, season, puzzleIdx]);

  const userRecord = useMemo(() => {
    return records.find((r) => r.you);
  }, [records]);

  // Top 3 Podium Winners
  const top1 = records[0];
  const top2 = records[1];
  const top3 = records[2];

  const scrollToMyRank = () => {
    if (userRowRef.current) {
      userRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightUserRow(true);
      setTimeout(() => setHighlightUserRow(false), 2800);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Rule string based on active game
  const ruleText = useMemo(() => {
    switch (activeGame) {
      case 'draw':
        return 'Xếp theo điểm vẽ và đoán hợp lệ từ cao xuống thấp.';
      case 'puzzle':
        return 'Cùng độ khó kiệt tác · Thời gian nhanh nhất; hòa thời gian xét ít thao tác.';
      case 'competition':
        return 'Thi tuần trực tiếp · Tầng cao nhất → số câu đúng → thời gian leo tháp.';
      case 'tower':
      default:
        return 'Leo tháp thường · Tầng cao nhất → số câu đúng → thời gian leo tháp.';
    }
  }, [activeGame]);

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/85 backdrop-blur-md animate-fade-in select-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leaderboard-title"
    >
      <div className="relative w-full max-w-4xl max-h-[94dvh] bg-gradient-to-b from-[#24131b] via-[#1a0e14] to-[#140a10] border-2 border-[#c4974f] rounded-2xl sm:rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.85)] flex flex-col overflow-hidden text-[#fff4dd]">
        {/* Traditional Vietnamese Corner Lotus Accents */}
        <CornerLotusOrnament position="top-left" />
        <CornerLotusOrnament position="top-right" />
        <CornerLotusOrnament position="bottom-left" />
        <CornerLotusOrnament position="bottom-right" />

        {/* ================= HEADER BẢNG VÀNG DI SẢN ================= */}
        <div className="relative z-10 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-[#c4974f]/30 bg-gradient-to-r from-[#2d1822] via-[#351a28] to-[#2d1822]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Gilded Medallion Icon */}
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#ffd572] via-[#e6ba55] to-[#996b18] text-[#291708] flex items-center justify-center shadow-[0_4px_16px_rgba(230,186,85,0.4)] border border-[#fff2cc] shrink-0">
                <Trophy className="w-6 h-6 sm:w-7 sm:h-7 drop-shadow-sm" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-[#e6ba55] px-2 py-0.5 rounded-full bg-[#e6ba55]/15 border border-[#e6ba55]/30">
                    BẢNG VÀNG DI SẢN MỸ THUẬT
                  </span>
                  <span className="text-[10px] text-[#e5d4b9]/70 font-semibold hidden sm:inline">
                    · TOP 100 DANH SĨ
                  </span>
                </div>
                <h2
                  id="leaderboard-title"
                  className="text-lg sm:text-2xl font-serif font-black text-white tracking-wide mt-0.5 truncate"
                >
                  {LEADERBOARD_TITLES[activeGame] || 'Bảng Xếp Hạng'}
                </h2>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-[#3d222e] hover:bg-[#522e3e] active:scale-95 text-[#fff4dd] border border-[#c4974f]/50 flex items-center justify-center transition-transform shrink-0 shadow-md"
              aria-label="Đóng bảng xếp hạng"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Ornamental Cloud Motif Divider */}
          <div className="mt-2.5">
            <DaiVietLotusCloudDivider />
          </div>
        </div>

        {/* ================= GAME TABS (SƠN MÀI KHẢM KIM) ================= */}
        <div className="flex border-b border-[#c4974f]/25 overflow-x-auto bg-[#1a0e14] px-3 sm:px-6 pt-2 gap-1.5 sm:gap-2 text-xs font-bold scrollbar-none">
          {[
            { key: 'tower', label: 'Leo tháp (Thường)', icon: '🏰' },
            { key: 'competition', label: 'Leo tháp (Thi tuần)', icon: '🏆' },
            { key: 'draw', label: 'Vẽ & đoán tranh', icon: '🎨' },
            { key: 'puzzle', label: 'Ghép tranh di sản', icon: '🧩' },
          ].map(({ key, label, icon }) => {
            const isActive = activeGame === key;
            return (
              <button
                key={key}
                onClick={() => setActiveGame(key as any)}
                className={`px-3 sm:px-4 py-2.5 rounded-t-xl transition-all whitespace-nowrap flex items-center gap-1.5 border-t border-x ${
                  isActive
                    ? 'bg-[#24131b] text-[#ffd572] border-[#c4974f]/70 font-black shadow-lg -mb-[1px] relative z-10'
                    : 'bg-transparent text-[#c4b3a4] hover:text-[#fff4dd] border-transparent hover:bg-[#2b1822]/50'
                }`}
              >
                <span>{icon}</span>
                <span>{label}</span>
                {isActive && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e6ba55] ml-0.5 animate-pulse" />
                )}
              </button>
            );
          })}
        </div>

        {/* ================= CONTROL TOOLBAR (PHẠM VI, MÀN CHƠI & TỚI HẠNG CỦA TÔI) ================= */}
        <div className="px-3 sm:px-6 py-2.5 bg-[#211118] border-b border-[#c4974f]/20 flex flex-wrap items-center justify-between gap-2.5 text-xs">
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-4">
            {/* Season / Scope Select */}
            <div className="flex items-center gap-1.5 bg-[#170c12] px-2.5 py-1.5 rounded-lg border border-[#c4974f]/30">
              <Calendar className="w-3.5 h-3.5 text-[#e6ba55]" />
              <span className="text-[#c4b3a4] font-medium hidden sm:inline">Phạm vi:</span>
              <select
                value={activeGame === 'competition' ? 'season' : season}
                disabled={activeGame === 'competition'}
                onChange={(e) => setSeason(e.target.value as 'all' | 'season')}
                className="bg-transparent text-[#ffd572] font-bold focus:outline-none cursor-pointer text-xs"
              >
                <option value="all" className="bg-[#24131b] text-white">Toàn thời gian</option>
                <option value="season" className="bg-[#24131b] text-white">
                  {activeGame === 'competition' ? 'Tuần 07–13.09.2026' : 'Mùa khám phá · Mẫu'}
                </option>
              </select>
            </div>

            {/* Puzzle Stage Select */}
            {activeGame === 'puzzle' && (
              <div className="flex items-center gap-1.5 bg-[#170c12] px-2.5 py-1.5 rounded-lg border border-[#c4974f]/30">
                <Layers className="w-3.5 h-3.5 text-[#e6ba55]" />
                <span className="text-[#c4b3a4] font-medium hidden sm:inline">Kiệt tác:</span>
                <select
                  value={puzzleIdx}
                  onChange={(e) => setPuzzleIdx(Number(e.target.value))}
                  className="bg-transparent text-[#ffd572] font-bold focus:outline-none cursor-pointer text-xs max-w-[140px] sm:max-w-none truncate"
                >
                  {PUZZLE_LEVELS.map((title, i) => (
                    <option key={i} value={i} className="bg-[#24131b] text-white">
                      {title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Rules Note */}
            <div className="hidden md:flex items-center gap-1.5 text-[11px] text-[#c4b3a4]">
              <Info className="w-3 h-3 text-[#e6ba55]/80 shrink-0" />
              <span className="italic">{ruleText}</span>
            </div>
          </div>

          {/* Jump To My Rank CTA Button */}
          <button
            onClick={scrollToMyRank}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-[#e6ba55] to-[#c99738] text-[#291708] rounded-lg font-black text-xs hover:brightness-110 active:scale-95 transition-all shadow-[0_2px_8px_rgba(230,186,85,0.3)] ml-auto"
          >
            <Target className="w-3.5 h-3.5" />
            <span>Vị trí của tôi ({userRecord ? `#${userRecord.rank}` : '—'})</span>
          </button>
        </div>

        {/* ================= SCROLLABLE CONTENT BODY ================= */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-4 bg-[#140a10]">
          {/* ================= PODIUM TOP 3 DANH DỰ (TAM KHÔI BẢNG VÀNG) ================= */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3.5 max-w-2xl mx-auto pt-1 sm:pt-2">
            {/* Á QUÂN (RANK 2 - BẢNG NHÃN BẠCH NGỌC) */}
            {top2 && (
              <div className="order-1 flex flex-col items-center justify-between p-2.5 sm:p-3.5 rounded-2xl bg-gradient-to-b from-[#252a36] via-[#1a1e28] to-[#12151d] border border-[#94a3b8]/70 shadow-lg text-center relative overflow-hidden group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#cbd5e1] to-[#64748b] text-[#0f172a] font-serif font-black flex items-center justify-center text-xs sm:text-sm shadow border border-white/80 shrink-0">
                  02
                </div>
                <div className="mt-1.5 min-w-0 w-full">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#cbd5e1] flex items-center justify-center gap-1">
                    <Medal className="w-3 h-3 text-[#cbd5e1]" />
                    <span>Á QUÂN</span>
                  </span>
                  <strong className="text-xs sm:text-sm font-bold text-white block truncate mt-0.5">
                    {top2.name}
                  </strong>
                  <span className="text-[11px] sm:text-xs font-mono font-black text-[#93c5fd] block mt-0.5">
                    {top2.metric}
                  </span>
                  <small className="text-[9px] sm:text-[10px] text-[#94a3b8] block truncate mt-0.5">
                    {top2.detail}
                  </small>
                </div>
                {top2.you && (
                  <span className="mt-1.5 px-2 py-0.5 rounded text-[9px] bg-[#e6ba55] text-[#241307] font-black uppercase shadow">
                    BẠN
                  </span>
                )}
              </div>
            )}

            {/* QUÁN QUÂN (RANK 1 - TRẠNG NGUYÊN KIM BẢNG - ELEVATED) */}
            {top1 && (
              <div className="order-2 flex flex-col items-center justify-between p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-[#4a2610] via-[#2f1816] to-[#1f0f13] border-2 border-[#ffd572] shadow-[0_0_24px_rgba(230,186,85,0.4)] text-center relative overflow-hidden transform -translate-y-1 sm:-translate-y-2">
                {/* Crown & Sparkles badge */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 flex items-center gap-1 justify-center text-[#ffd572]">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                  <Crown className="w-5 h-5 filter drop-shadow animate-bounce" />
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#ffe082] via-[#e6ba55] to-[#996515] text-[#291708] font-serif font-black flex items-center justify-center text-sm sm:text-base shadow-xl border-2 border-[#fff7da] mt-2 shrink-0">
                  01
                </div>
                <div className="mt-1.5 min-w-0 w-full">
                  <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-[#ffd572] flex items-center justify-center gap-1">
                    <span>👑 QUÁN QUÂN</span>
                  </span>
                  <strong className="text-sm sm:text-base font-serif font-black text-white block truncate mt-0.5">
                    {top1.name}
                  </strong>
                  <span className="text-xs sm:text-sm font-mono font-black text-[#ffd572] block mt-0.5">
                    {top1.metric}
                  </span>
                  <small className="text-[10px] sm:text-[11px] text-[#e5d4b9] block truncate mt-0.5">
                    {top1.detail}
                  </small>
                </div>
                {top1.you && (
                  <span className="mt-1.5 px-2.5 py-0.5 rounded text-[10px] bg-[#e6ba55] text-[#241307] font-black uppercase shadow">
                    BẠN
                  </span>
                )}
              </div>
            )}

            {/* QUÝ QUÂN (RANK 3 - THÁM HOA ĐỒNG CỔ) */}
            {top3 && (
              <div className="order-3 flex flex-col items-center justify-between p-2.5 sm:p-3.5 rounded-2xl bg-gradient-to-b from-[#382319] via-[#241613] to-[#150c0c] border border-[#d97706]/70 shadow-lg text-center relative overflow-hidden group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#d97706] to-[#78350f] text-[#ffedd5] font-serif font-black flex items-center justify-center text-xs sm:text-sm shadow border border-[#fdba74] shrink-0">
                  03
                </div>
                <div className="mt-1.5 min-w-0 w-full">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#fdba74] flex items-center justify-center gap-1">
                    <Award className="w-3 h-3 text-[#fdba74]" />
                    <span>QUÝ QUÂN</span>
                  </span>
                  <strong className="text-xs sm:text-sm font-bold text-white block truncate mt-0.5">
                    {top3.name}
                  </strong>
                  <span className="text-[11px] sm:text-xs font-mono font-black text-[#fbbf24] block mt-0.5">
                    {top3.metric}
                  </span>
                  <small className="text-[9px] sm:text-[10px] text-[#fed7aa] block truncate mt-0.5">
                    {top3.detail}
                  </small>
                </div>
                {top3.you && (
                  <span className="mt-1.5 px-2 py-0.5 rounded text-[9px] bg-[#e6ba55] text-[#241307] font-black uppercase shadow">
                    BẠN
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ================= PERSONAL SCORE BANNER ================= */}
          {userRecord && (
            <div className="p-3 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#17382d] via-[#102d24] to-[#17382d] border-2 border-[#34d399]/70 shadow-lg flex items-center justify-between gap-3 text-xs sm:text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#34d399]/20 border border-[#34d399]/50 flex items-center justify-center font-serif font-black text-[#6ee7b7] text-sm sm:text-base shrink-0">
                  #{userRecord.rank}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#ecfdf5] truncate">{userRecord.name}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] bg-[#e6ba55] text-[#2b1708] font-black uppercase shrink-0">
                      BẠN ĐANG ĐỨNG HẠNG {userRecord.rank}
                    </span>
                  </div>
                  <span className="text-[11px] sm:text-xs text-[#a7f3d0] block truncate mt-0.5">
                    Thành tích: {userRecord.metric} · {userRecord.detail}
                  </span>
                </div>
              </div>
              <button
                onClick={scrollToMyRank}
                className="shrink-0 px-3 sm:px-4 py-1.5 rounded-lg bg-[#e6ba55] hover:bg-[#ffd572] active:scale-95 text-[#241307] font-black text-xs flex items-center gap-1.5 shadow transition-transform"
              >
                <Target className="w-3.5 h-3.5" />
                <span>Xem hàng của tôi</span>
              </button>
            </div>
          )}

          {/* ================= TOP 100 ROSTER TABLE ================= */}
          <div className="rounded-xl sm:rounded-2xl border border-[#c4974f]/30 overflow-hidden bg-[#1a0e14] shadow-xl">
            <table className="w-full text-left text-xs sm:text-sm border-separate border-spacing-y-1 p-1.5 sm:p-2.5">
              <thead>
                <tr className="text-[10px] sm:text-[11px] font-black text-[#c4974f] uppercase tracking-wider">
                  <th className="py-2.5 px-3 w-14 sm:w-20 text-center">Hạng</th>
                  <th className="py-2.5 px-3">Danh Sĩ</th>
                  <th className="py-2.5 px-3 text-right">Thành Tích</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const isTop1 = r.rank === 1;
                  const isTop2 = r.rank === 2;
                  const isTop3 = r.rank === 3;
                  const isTop10 = r.rank <= 10;

                  return (
                    <tr
                      key={r.rank}
                      ref={r.you ? userRowRef : null}
                      className={`transition-all duration-300 rounded-xl ${
                        r.you
                          ? highlightUserRow
                            ? 'bg-[#1e4a3c] text-white ring-4 ring-[#e6ba55] shadow-[0_0_25px_rgba(230,186,85,0.7)] font-bold scale-[1.01]'
                            : 'bg-gradient-to-r from-[#183d31] to-[#123026] text-[#e6fbf2] font-bold border-2 border-[#34d399]/80 shadow-md'
                          : isTop1
                          ? 'bg-[#3b2014]/70 hover:bg-[#482819] text-[#fff4dd]'
                          : isTop2
                          ? 'bg-[#212634]/70 hover:bg-[#2c3346] text-[#fff4dd]'
                          : isTop3
                          ? 'bg-[#2d1c14]/70 hover:bg-[#3d261b] text-[#fff4dd]'
                          : r.rank % 2 === 0
                          ? 'bg-[#24131b]/60 hover:bg-[#301a24] text-[#fff4dd]'
                          : 'bg-[#1c0f16]/60 hover:bg-[#301a24] text-[#fff4dd]'
                      }`}
                    >
                      {/* Rank Number / Badge */}
                      <td className="py-2.5 px-2 sm:px-3 text-center">
                        <span
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg mx-auto flex items-center justify-center font-serif font-black text-xs ${
                            isTop1
                              ? 'bg-[#e6ba55] text-[#291708] shadow-[0_0_8px_rgba(230,186,85,0.6)]'
                              : isTop2
                              ? 'bg-[#cbd5e1] text-[#0f172a] shadow'
                              : isTop3
                              ? 'bg-[#d97706] text-white shadow'
                              : r.you
                              ? 'bg-[#34d399] text-[#06241b]'
                              : isTop10
                              ? 'bg-[#2f1924] text-[#ffd572] border border-[#e6ba55]/40'
                              : 'bg-white/5 text-[#c4b3a4]'
                          }`}
                        >
                          {String(r.rank).padStart(2, '0')}
                        </span>
                      </td>

                      {/* Player Info */}
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white truncate text-xs sm:text-sm">
                            {r.name}
                          </span>
                          {r.you && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-[#e6ba55] text-[#291708] font-black uppercase tracking-wider shrink-0 shadow">
                              BẠN
                            </span>
                          )}
                          {isTop10 && !r.you && (
                            <span className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[8px] bg-white/10 text-[#ffd572] font-semibold uppercase">
                              Top 10
                            </span>
                          )}
                        </div>
                        <small className="text-[11px] text-[#c4b3a4] block mt-0.5 truncate">
                          {r.detail}
                        </small>
                      </td>

                      {/* Score Metric */}
                      <td className="py-2.5 px-3 text-right">
                        <span className="font-mono font-black text-xs sm:text-sm text-[#ffd572] block">
                          {r.metric}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="p-3.5 sm:p-4 bg-gradient-to-r from-[#24131b] via-[#2d1822] to-[#24131b] border-t border-[#c4974f]/30 flex items-center justify-between text-xs text-[#c4b3a4] pb-[max(0.875rem,env(safe-area-inset-bottom,14px))]">
          <span className="truncate pr-2">
            Top 100 cập nhật tự động · Bách gia tranh tài VNFAM
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#3d222e] hover:bg-[#522e3e] active:scale-95 border border-[#c4974f]/60 text-white font-bold transition-all shadow shrink-0"
          >
            Đóng Bảng Vàng
          </button>
        </div>
      </div>
    </div>
  );
};
